/**
 * Outcome surface — calm, reflective, validating.
 *
 * Per Sprint B locked direction:
 *   - Single KCIS™ aesthetic; subtle purple accent only (no per-door recoloring).
 *   - No clinical / diagnostic language.
 *   - Resonance copy comes verbatim from backend `compliance_language`.
 *   - CTA hierarchy: primary aligned action → secondary educational → restart subtle.
 */
import { Sparkles, ArrowRight, BookOpen, ExternalLink } from "lucide-react";

const DOOR_DISPLAY = {
  clinic: "Clinic",
  coaching: "Coaching",
  university: "Learning & Growth",
  comfort_care: "Comfort Care",
  integrated: "Integrated Pathway",
};

const DOOR_LEAD = {
  clinic: "A practitioner-supported path for the body's reflections.",
  coaching: "A reflective space designed for the heart and the inner life.",
  university: "An invitation to learn, grow, and step into a new chapter.",
  comfort_care: "Gentle support for caregivers and the loved ones they hold.",
  integrated: "A blended doorway — honoring more than one part of your life at once.",
};

const READINESS_DISPLAY = {
  nurture: "Taking it gently",
  exploring: "Exploring",
  ready: "Ready",
  committed: "Committed",
};

const INTENSITY_DISPLAY = {
  calm: "Calm",
  stirring: "Stirring",
  elevated: "Elevated",
  acute: "Tender",
};

function CtaButton({ cta }) {
  const isExternal = !!cta?.target_url;
  const target = cta?.target_url || (cta?.funnel_key ? `/funnels/${cta.funnel_key.replace(/_/g, "-")}` : "/");
  return (
    <a
      href={target}
      target={isExternal ? "_blank" : undefined}
      rel={isExternal ? "noopener noreferrer" : undefined}
      data-testid="kcis-outcome-cta-primary"
      className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-purple-900 hover:bg-purple-800 text-white text-base tracking-wide shadow-md hover:shadow-lg transition"
    >
      <span>{cta?.label || "Continue →"}</span>
      {isExternal ? <ExternalLink className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
    </a>
  );
}

function SecondaryAction() {
  return (
    <a
      href="/wellness-assessment"
      data-testid="kcis-outcome-cta-secondary"
      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-purple-200 text-purple-900 hover:bg-purple-50 text-sm transition"
    >
      <BookOpen className="h-4 w-4" />
      <span>Explore other reflections</span>
    </a>
  );
}

export default function OutcomeScreen({ outcome, firstName, onStartOver }) {
  if (!outcome) return null;
  const door = outcome.assigned_door || "integrated";
  const profile = outcome.emotional_identity_profile || {};
  const readiness = outcome.readiness || {};
  const cta = outcome.recommended_cta || {};
  const offerDisplay = cta.offer_display;
  const offerPrice = cta.offer_price;

  return (
    <article className="space-y-6" data-testid="kcis-outcome-screen">
      {/* Hero */}
      <section className="text-center kcis-fade-in px-2">
        <p className="kcis-assessment-overline mb-3">Your reflection</p>
        <h1 className="kcis-assessment-display text-3xl sm:text-4xl lg:text-5xl leading-tight mb-3">
          {firstName ? `Here's what we're sensing for you, ${firstName}.` : "Here's what we're sensing for you."}
        </h1>
        <p className="text-base text-slate-600 max-w-xl mx-auto">
          Take this as a starting point — not a destination.
        </p>
      </section>

      {/* Door card */}
      <section className="kcis-assessment-card p-7 sm:p-9 text-center kcis-fade-in-delay-1" data-testid="kcis-outcome-door">
        <div className="kcis-door-pill mb-4">
          <Sparkles className="h-3.5 w-3.5" />
          Your Recommended Doorway
        </div>
        <h2 className="kcis-assessment-display text-3xl sm:text-4xl mb-3" data-testid="kcis-outcome-door-name">
          {DOOR_DISPLAY[door] || "Your Pathway"}
        </h2>
        <p className="text-base text-slate-600 max-w-lg mx-auto leading-relaxed">
          {DOOR_LEAD[door] || "A reflective doorway shaped by your responses."}
        </p>
      </section>

      {/* Emotional identity callout */}
      {profile.compliance_language && (
        <section
          className="kcis-assessment-card p-7 sm:p-9 kcis-fade-in-delay-1"
          data-testid="kcis-outcome-identity"
        >
          <p className="kcis-assessment-overline mb-3">A reflection on who you are right now</p>
          <p className="text-lg sm:text-xl text-slate-800 leading-relaxed mb-5" data-testid="kcis-outcome-resonance">
            {profile.compliance_language}
          </p>
          <div className="flex flex-wrap gap-2">
            {profile.emotional_intensity && (
              <span
                className="inline-flex items-center px-3 py-1 rounded-full bg-purple-50 text-purple-900 text-xs border border-purple-100"
                data-testid="kcis-outcome-intensity-pill"
              >
                Emotional weather · {INTENSITY_DISPLAY[profile.emotional_intensity] || profile.emotional_intensity}
              </span>
            )}
            {readiness.band && (
              <span
                className="inline-flex items-center px-3 py-1 rounded-full bg-purple-50 text-purple-900 text-xs border border-purple-100"
                data-testid="kcis-outcome-readiness-pill"
              >
                Readiness · {READINESS_DISPLAY[readiness.band] || readiness.band}
              </span>
            )}
          </div>
        </section>
      )}

      {/* Offer card */}
      {(offerDisplay || cta.label) && (
        <section
          className="kcis-assessment-card p-7 sm:p-9 kcis-fade-in-delay-2"
          data-testid="kcis-outcome-offer"
        >
          <p className="kcis-assessment-overline mb-3">A suggested next step</p>
          {offerDisplay && (
            <h3 className="kcis-assessment-display text-xl sm:text-2xl mb-1" data-testid="kcis-outcome-offer-display">
              {offerDisplay}
            </h3>
          )}
          {offerPrice && (
            <p className="text-sm text-slate-500" data-testid="kcis-outcome-offer-price">
              {offerPrice}
            </p>
          )}
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-start">
            <CtaButton cta={cta} />
          </div>
          <div className="mt-4 flex justify-center sm:justify-start">
            <SecondaryAction />
          </div>
        </section>
      )}

      {/* Restart (subtle) + disclaimer */}
      <section className="text-center pt-2 pb-8 kcis-fade-in-delay-2">
        <button
          type="button"
          onClick={onStartOver}
          className="text-xs text-slate-500 hover:text-purple-900 underline underline-offset-2 transition"
          data-testid="kcis-outcome-start-over"
        >
          Start a new reflection
        </button>
        <p className="mt-6 text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
          {outcome.compliance_disclaimer ||
            "Wellness pattern signals are educational information only. They are not a diagnosis and do not replace clinical care."}
        </p>
      </section>
    </article>
  );
}
